import { REQUIRED_LIKERT_QUESTION_KEYWORDS, Question } from '@/modules/shared/hr-form-engine';
import {
  allQuestions,
  parseFormSnapshot,
  PmReviewDetail,
  REVIEWER_GROUPS,
  ReviewerGroup,
  SKILLS_LIKERT_QUESTION,
} from '@/modules/performance/hr-review-core';

/** Topic rows of the qualitative matrix, in the legacy reporting order (six required topics + peer-chosen skills). */
const QUALITATIVE_CATEGORIES = [...REQUIRED_LIKERT_QUESTION_KEYWORDS, SKILLS_LIKERT_QUESTION];

export interface CategoryAverage {
  Category: string;
  /** Scale length of the questions found for this category (metadata only - scores are already normalized). */
  MaxScale: number;
  AverageByReviewerGroup: Record<ReviewerGroup, number>;
}

export interface QualitativeResult {
  categoryAverages: CategoryAverage[];
  /** Completed reviews per reviewer group. */
  submittedByGroup: Record<ReviewerGroup, number>;
  totalReviews: number;
  totalGroups: number;
}

/** Legacy ResolveReviewerGroup: which column of the matrix a completed review counts toward. */
export const resolveReviewerGroup = (detail: Pick<PmReviewDetail, 'ReviewType' | 'Category'>): ReviewerGroup | null => {
  switch (detail.ReviewType) {
    case 'self':
      return 'Self';
    case 'management':
    case 'annual':
    case 'confirmation':
    case 'half_yearly':
    case 'special':
      return 'Head';
    case 'peer':
      switch (detail.Category) {
        case 'Senior':
          return 'Senior Peer';
        case 'Equal':
          return 'Equal Peer';
        case 'Junior':
          return 'Junior Peer';
        case 'Other':
          return 'Other Peer';
        default:
          return null;
      }
    default:
      return null;
  }
};

/** Legacy ResolveQuestionCategory: a topic keyword contained in the question text, else "Skills" -> Skills Assessment. */
export const resolveQuestionCategory = (questionText: string): string | null => {
  if (!questionText.trim()) return null;
  const lower = questionText.toLowerCase();
  const topic = REQUIRED_LIKERT_QUESTION_KEYWORDS.find((keyword) => lower.includes(keyword.toLowerCase()));
  if (topic) return topic;
  return lower.includes('skills') ? SKILLS_LIKERT_QUESTION : null;
};

/** A numeric answer is used as-is; otherwise the answer is scored by its 1-based position among the question's labels. */
const resolveLikertScore = (value: string, labels: string[]): number | null => {
  const numeric = Number(value);
  if (value.trim() !== '' && Number.isFinite(numeric)) return numeric;
  const index = labels.findIndex((label) => label.toLowerCase() === value.toLowerCase());
  return index >= 0 ? index + 1 : null;
};

/** Rescales "3 of 5" and "3 of 4" onto the common scale so questions with different lengths can be averaged. */
export const normalizeScore = (score: number, maxScale: number, normalizedMax: number) =>
  maxScale <= 0 || maxScale === normalizedMax ? score : (score * normalizedMax) / maxScale;

const likertScores = (question: Question, normalizedMax: number): { score: number; maxScale: number }[] => {
  const maxScale = question.LikertLabels?.length ?? 0;
  return (question.LikertStatements ?? []).flatMap((statement) => {
    const value = statement.Value?.find((entry) => entry && entry.trim());
    if (value === undefined) return [];
    const score = resolveLikertScore(value, question.LikertLabels ?? []);
    return score === null ? [] : [{ score: normalizeScore(score, maxScale, normalizedMax), maxScale }];
  });
};

const emptyGroupRecord = (): Record<ReviewerGroup, number> =>
  Object.fromEntries(REVIEWER_GROUPS.map((group) => [group, 0])) as Record<ReviewerGroup, number>;

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * Legacy CalculateQualitativeScores: averages every Likert answer of the completed reviews by topic (row) and
 * reviewer group (column), after normalizing each answer to the configured common scale.
 */
export const calculateQualitative = (
  completedDetails: Pick<PmReviewDetail, 'ReviewType' | 'Category' | 'PmFormJson'>[],
  normalizedMax: number
): QualitativeResult => {
  const totals = new Map<string, { total: number; count: number }>();
  const maxScales = new Map<string, number>();
  const submittedByGroup = emptyGroupRecord();

  completedDetails.forEach((detail) => {
    const group = resolveReviewerGroup(detail);
    if (!group) return;
    submittedByGroup[group] += 1;

    const parsed = parseFormSnapshot(detail.PmFormJson);
    if (!parsed) return;
    allQuestions(parsed.definition)
      .filter((question) => question.FormatType === 'likert-scale')
      .forEach((question) => {
        const category = resolveQuestionCategory(question.Question);
        if (!category) return;
        likertScores(question, normalizedMax).forEach(({ score, maxScale }) => {
          const key = `${category}|${group}`;
          const current = totals.get(key) ?? { total: 0, count: 0 };
          totals.set(key, { total: current.total + score, count: current.count + 1 });
          maxScales.set(category, Math.max(maxScales.get(category) ?? 0, maxScale));
        });
      });
  });

  const categoryAverages = QUALITATIVE_CATEGORIES.map((category) => ({
    Category: category,
    MaxScale: maxScales.get(category) ?? 0,
    AverageByReviewerGroup: Object.fromEntries(
      REVIEWER_GROUPS.map((group) => {
        const entry = totals.get(`${category}|${group}`);
        return [group, entry && entry.count > 0 ? round2(entry.total / entry.count) : 0];
      })
    ) as Record<ReviewerGroup, number>,
  }));

  return {
    categoryAverages,
    submittedByGroup,
    totalReviews: completedDetails.length,
    totalGroups: REVIEWER_GROUPS.filter((group) => submittedByGroup[group] > 0).length,
  };
};
